import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { querySuiteRuns } from '@/features/executions/data';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildSuiteRunPath } from '@/features/shared/components/tableSystem/tableRoutes';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import { TableV2NestedEmptyState } from '@/features/shared/components/tableSystem/TableV2NestedEmptyState';
import { TableV2NestedFrame } from '@/features/shared/components/tableSystem/TableV2NestedFrame';
import {
  buildTableV2NestedBodyRowSx,
  queryAllTableV2NestedRows,
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2NameCell,
  TableV2NestedCollapse,
  TableV2PassRateCell,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { formatTableV2RunNumber } from '@/features/shared/components/tableSystem/TableV2RunFormatting';
import { TableV2SkeletonRow } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import {
  buildNestedSuiteRunFilters,
  countSuiteRunTests,
  defaultNestedSuiteRunSort,
} from '@/features/suites/components/SuitesTableV2.helpers';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@/ui/primitives';
import { SuiteRunExpandedTests } from './SuiteRunExpandedTests';
import { buildSuiteRunHeader, type SortableNestedSuiteRunField } from './SuitesTableV2Headers';
import { buildSuiteRunColgroup, buildSuiteRunSkeletonColumns } from './SuitesTableV2Layout';
import type { TagColor } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, SuiteRunDto, TestRunDto } from '@flowqa/shared';

const SuiteRunNestedRow = ({
  actionsDisabled,
  deleteRowPendingId,
  isLastVisibleRow,
  linksDisabled,
  row,
  showActions,
  sortingDisabled,
  tagColorByName,
  testRunsQueryFn,
  testRunDeleteRowPendingId,
  testRunUpdateRowTagsColorPendingId,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onDeleteTestRunRow,
  onEditRow,
  onEditTestRunRow,
  onUpdateRowTagsColor,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  linksDisabled: boolean;
  row: SuiteRunDto;
  showActions: boolean;
  sortingDisabled: boolean;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  testRunDeleteRowPendingId: string | null;
  testRunUpdateRowTagsColorPendingId: string | null;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: SuiteRunDto) => void;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onEditRow: (row: SuiteRunDto) => void;
  onEditTestRunRow: (row: TestRunDto) => void;
  onUpdateRowTagsColor: (input: { row: SuiteRunDto; tags: string[]; colors: TagColor[] }) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const suiteName = row.displayName ?? row.suiteName ?? row.name ?? row.id;
  const suiteRunNumberLabel = formatTableV2RunNumber(row.runNumber) ?? '-';
  const rowName = suiteRunNumberLabel === '-' ? suiteName : suiteRunNumberLabel;
  const suiteRunHref = buildSuiteRunPath(row.suiteSlug ?? row.suiteId, row.runNumber);
  const testCount = countSuiteRunTests(row).total;

  return (
    <>
      <TableRow
        sx={buildTableV2NestedBodyRowSx({
          depth: 1,
          hasRenderedDivider: !expanded && !isLastVisibleRow,
        })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse test runs' : 'Expand test runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={linksDisabled ? null : suiteRunHref}
            primary={suiteRunNumberLabel}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <TableV2PassRateCell counts={countSuiteRunTests(row)} />
        </TableCell>
        <TableCell>
          <TableV2DurationCell value={row.durationMs} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.timestamp} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        {showActions ? (
          <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
            {actionsDisabled ? null : (
              <TableActionsCell
                rowName={rowName}
                verticalAlign="center"
                deleteActionLabel={getEntityActionLabel('delete', 'Suite Run')}
                editActionLabel={getEntityActionLabel('edit', 'Suite Run')}
                editTagsEntityNoun="suite run"
                rowTags={row.tags ?? []}
                onEdit={() => {
                  onEditRow(row);
                }}
                onUpdateTagsColor={(input) => {
                  onUpdateRowTagsColor({ row, ...input });
                }}
                updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
                onDelete={() => {
                  onDeleteRow(row);
                }}
                disableDelete={deleteRowPendingId === row.id}
              />
            )}
          </TableCell>
        ) : null}
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={showActions ? 8 : 7}>
          <SuiteRunExpandedTests
            actionsDisabled={actionsDisabled}
            childCount={testCount}
            deleteRowPendingId={testRunDeleteRowPendingId}
            expanded={expanded}
            linksDisabled={linksDisabled}
            organisationId={row.organisationId}
            projectId={row.projectId}
            showActions={showActions}
            sortingDisabled={sortingDisabled}
            tagColorByName={tagColorByName}
            suiteRunId={row.id}
            testRunsQueryFn={testRunsQueryFn}
            updateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
            onDeleteTestRunRow={onDeleteTestRunRow}
            onEditTestRunRow={onEditTestRunRow}
            onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const SuiteExpandedRuns = ({
  actionsDisabled,
  childCount,
  deleteRowPendingId,
  expanded,
  linksDisabled,
  organisationId,
  projectId,
  showActions = true,
  suiteId,
  latestOnly,
  latestRunId,
  suiteRunsScopeKey,
  suiteRunsQueryFn,
  sortingDisabled,
  tagColorByName,
  testRunsQueryFn,
  testRunDeleteRowPendingId,
  testRunUpdateRowTagsColorPendingId,
  updateRowTagsColorPendingId,
  onDeleteSuiteRunRow,
  onDeleteTestRunRow,
  onEditSuiteRunRow,
  onEditTestRunRow,
  onUpdateSuiteRunRowTagsColor,
  onUpdateTestRunRowTagsColor,
}: {
  actionsDisabled: boolean;
  childCount: number;
  deleteRowPendingId: string | null;
  expanded: boolean;
  linksDisabled: boolean;
  organisationId: string | null;
  projectId: string;
  showActions?: boolean;
  suiteId: string;
  latestOnly: boolean;
  latestRunId: string | null;
  suiteRunsScopeKey?: string;
  suiteRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<SuiteRunDto>>;
  sortingDisabled: boolean;
  tagColorByName: Partial<Record<string, TagColor>>;
  testRunsQueryFn?: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  testRunDeleteRowPendingId: string | null;
  testRunUpdateRowTagsColorPendingId: string | null;
  updateRowTagsColorPendingId: string | null;
  onDeleteSuiteRunRow: (row: SuiteRunDto) => void;
  onDeleteTestRunRow: (row: TestRunDto) => void;
  onEditSuiteRunRow: (row: SuiteRunDto) => void;
  onEditTestRunRow: (row: TestRunDto) => void;
  onUpdateSuiteRunRowTagsColor: (input: {
    row: SuiteRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  onUpdateTestRunRowTagsColor: (input: {
    row: TestRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
}): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultNestedSuiteRunSort);
  const handleSortChange = (field: SortableNestedSuiteRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const header = buildSuiteRunHeader({
    includeActions: showActions,
    sort,
    sortingDisabled,
    onSortChange: handleSortChange,
  });
  const colgroup = buildSuiteRunColgroup(showActions);
  const skeletonColumns = buildSuiteRunSkeletonColumns(showActions);
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.suiteRuns,
      'v2-nested',
      organisationId,
      projectId,
      suiteId,
      latestOnly ? (latestRunId ?? 'missing-latest-run') : 'all-runs',
      suiteRunsScopeKey ?? 'default',
      sort.field,
      sort.direction,
    ),
    enabled: expanded,
    queryFn: async () => {
      const input = {
        page: 1,
        pageSize: 1,
        sorts: [sort],
        filters: buildNestedSuiteRunFilters({ latestOnly, latestRunId, suiteId }),
      };
      const queryPage =
        latestOnly && latestRunId !== null
          ? async (nextInput: GridQueryInput) =>
              await querySuiteRuns(projectId, organisationId, nextInput)
          : (suiteRunsQueryFn ??
            (async (nextInput: GridQueryInput) =>
              await querySuiteRuns(projectId, organisationId, nextInput)));
      return await queryAllTableV2NestedRows(input, queryPage);
    },
    staleTime: 30_000,
  });
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <TableV2NestedFrame depth={1}>
            <Table aria-label="Suite runs loading" sx={tableV2NestedDepthSx[1].table}>
              {colgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`suite-runs-nested-skeleton-${String(index)}`}
                    columns={skeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : query.data && query.data.items.length > 0 ? (
          <TableV2NestedFrame depth={1}>
            <Table aria-label="Suite runs" sx={tableV2NestedDepthSx[1].table}>
              {colgroup}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((suiteRun, index, suiteRuns) => (
                  <SuiteRunNestedRow
                    key={suiteRun.id}
                    actionsDisabled={actionsDisabled}
                    deleteRowPendingId={deleteRowPendingId}
                    isLastVisibleRow={index === suiteRuns.length - 1}
                    linksDisabled={linksDisabled}
                    row={suiteRun}
                    showActions={showActions}
                    sortingDisabled={sortingDisabled}
                    tagColorByName={tagColorByName}
                    testRunsQueryFn={testRunsQueryFn}
                    testRunDeleteRowPendingId={testRunDeleteRowPendingId}
                    testRunUpdateRowTagsColorPendingId={testRunUpdateRowTagsColorPendingId}
                    updateRowTagsColorPendingId={updateRowTagsColorPendingId}
                    onDeleteRow={onDeleteSuiteRunRow}
                    onDeleteTestRunRow={onDeleteTestRunRow}
                    onEditRow={onEditSuiteRunRow}
                    onEditTestRunRow={onEditTestRunRow}
                    onUpdateRowTagsColor={onUpdateSuiteRunRowTagsColor}
                    onUpdateTestRunRowTagsColor={onUpdateTestRunRowTagsColor}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : (
          <TableV2NestedEmptyState
            ariaLabel="Suite runs"
            colgroup={colgroup}
            colSpan={showActions ? 8 : 7}
            depth={1}
            header={header}
          />
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};
